import { FC, Fragment } from 'react';

import GridItemList from 'components/GridItemList';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import NestedTable from 'components/tables/list-table/NestedTable';
import { getLinks } from 'pages/matters/util';
import { ContactMinDto } from 'types/contacts';
import { CorrespondenceListElementDto } from 'types/correspondence';
import { t } from 'utils/getTranslations';
import { useCorrespondenceParties } from './useCorrespondence';
import { getParties } from './util/parties';

const CorrespondenceTableItemExpanded: FC<CorrespondenceListElementDto> = (props) => {
    const { documentDate, note, parties, dmsLink, tasks } = props;

    const { clientParties, senderOrRecipientParties } = useCorrespondenceParties(parties);
    const isNewDesign = useIsNewDesign();

    const links = tasks.map((task, index) => {
        const optionalComma = index < tasks.length - 1 ? `,\u00A0` : null;
        return (
            <Fragment key={task.id}>
                <RecordLink
                    withOrigin
                    className="entity-link text-fg hover:text-link no-underline hover:underline"
                    to={`/task/${task.id}`}
                    label={task.name}
                />
                {optionalComma}
            </Fragment>
        );
    });

    const details = [
        {
            label: t('correspondence.document-date'),
            content:
                isNewDesign && documentDate ? <DateDefaultNowrap dateValue={documentDate} /> : (documentDate ?? '-'),
        },
        {
            label: t('correspondence.connected-client'),
            content: getLinks(
                'contact',
                clientParties
                    .filter(({ mainParty }) => mainParty !== null)
                    .map((party) => party.mainParty as ContactMinDto)
            ),
        },
        {
            label: t('correspondence.sender-recipient'),
            content: getLinks('contact', getParties(senderOrRecipientParties)),
        },
        {
            label: t('correspondence.connected-task'),
            content: links.length > 0 ? links : '-',
        },
        {
            label: t('correspondence.correspondence-note'),
            content: note ?? '-',
        },
        {
            label: t('correspondence.dms-link'),
            content: dmsLink ? (
                <a
                    className="entity-link text-fg hover:text-link break-all no-underline hover:underline"
                    href={dmsLink}
                >
                    {dmsLink}
                </a>
            ) : (
                '-'
            ),
        },
    ];

    // The new design draws the details as the sub-table My Portfolio opens under a group: one row, a column each.
    if (isNewDesign) {
        return (
            <NestedTable<CorrespondenceListElementDto>
                data={[props]}
                columns={details.map(({ label, content }) => ({ id: label, header: label, Cell: () => content }))}
                getRowKey={(row) => row.id}
            />
        );
    }

    return (
        <div className="-my-3">
            <GridItemList data={details} twoPerRow size={12} listItemClassName="mx-0 w-full" noContainer />
        </div>
    );
};

export default CorrespondenceTableItemExpanded;
